import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';
import type { TaskStatus } from './task-board.js';

export type { TaskStatus };

/**
 * Klient úkolů makléře (krok CRM2). Typy jsou generované z
 * docs/api/openapi.json — UI si `canUpdate` a `allowedTransitions` jen ČTE,
 * životní cyklus úkolu se v UI nedoplňuje (viz `task-board.ts`).
 */

export type TaskColumn = NonNullable<
  ApiPaths['/v1/crm/task-columns']['get']['responses']['200']['content']['application/json']
>['items'][number];

export type Task = ApiPaths['/v1/crm/tasks/{id}']['get']['responses']['200']['content']['application/json'];

export type TodayTask = NonNullable<
  ApiPaths['/v1/crm/tasks/today']['get']['responses']['200']['content']['application/json']
>['items'][number];

export type AutoTaskRunResult =
  ApiPaths['/v1/crm/tasks/auto']['post']['responses']['200']['content']['application/json'];

export type CreateTaskBody =
  ApiPaths['/v1/crm/tasks']['post']['requestBody']['content']['application/json'];
export type UpdateTaskBody =
  ApiPaths['/v1/crm/tasks/{id}']['patch']['requestBody']['content']['application/json'];

export async function listTaskColumns(): Promise<readonly TaskColumn[]> {
  const result = await unwrap(apiClient.GET('/v1/crm/task-columns', {}));
  return result.items;
}

export interface ListTasksParams {
  readonly status?: TaskStatus | undefined;
  readonly ownerUserId?: string | undefined;
  readonly caseId?: string | undefined;
  readonly partyId?: string | undefined;
  readonly demandId?: string | undefined;
  readonly originKind?: string | undefined;
  /** Hledany text — hleda SERVER (krok TOKYFIX2), ne jen nactena stranka. */
  readonly q?: string | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

/** Jeden požadavek na stránku; další stránky se v UI necházejí (viz hlášení oříznutí). */
export async function listTasks(
  params: ListTasksParams = {},
): Promise<{ items: Task[]; nextCursor: string | null }> {
  return unwrap(
    apiClient.GET('/v1/crm/tasks', {
      params: {
        query: {
          limit: params.limit ?? 100,
          ...(params.status !== undefined ? { status: params.status } : {}),
          ...(params.ownerUserId !== undefined ? { ownerUserId: params.ownerUserId } : {}),
          ...(params.caseId !== undefined ? { caseId: params.caseId } : {}),
          ...(params.partyId !== undefined ? { partyId: params.partyId } : {}),
          ...(params.demandId !== undefined ? { demandId: params.demandId } : {}),
          ...(params.originKind !== undefined ? { originKind: params.originKind } : {}),
          ...(params.q !== undefined && params.q !== '' ? { q: params.q } : {}),
          ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        },
      },
    }),
  );
}

export async function getTask(id: string): Promise<Task> {
  return unwrap(apiClient.GET('/v1/crm/tasks/{id}', { params: { path: { id } } }));
}

export interface CreateTaskInput {
  readonly title: string;
  readonly description?: string | undefined;
  readonly status?: TaskStatus | undefined;
  readonly dueAt?: string | null | undefined;
  readonly ownerUserId?: string | null | undefined;
  readonly caseId?: string | null | undefined;
  readonly partyId?: string | null | undefined;
  readonly demandId?: string | null | undefined;
}

/**
 * Tělo UI skládá podmíně (`...(hodnota === '' ? {} : { pole: hodnota })`), takže
 * do něj tečou i `undefined`. Převod je tady na jednom místě, ne v každém
 * volajícím — jinak by `exactOptionalPropertyTypes` rozbil typy.
 */
export async function createTask(input: CreateTaskInput): Promise<Task> {
  return unwrap(apiClient.POST('/v1/crm/tasks', { body: input as CreateTaskBody }));
}

export interface UpdateTaskInput {
  readonly title?: string | undefined;
  readonly description?: string | null | undefined;
  readonly dueAt?: string | null | undefined;
  readonly ownerUserId?: string | null | undefined;
  readonly caseId?: string | null | undefined;
  readonly partyId?: string | null | undefined;
  readonly demandId?: string | null | undefined;
}

export async function updateTask(id: string, input: UpdateTaskInput): Promise<Task> {
  return unwrap(apiClient.PATCH('/v1/crm/tasks/{id}', { params: { path: { id } }, body: input as UpdateTaskBody }));
}

export async function transitionTask(id: string, toStatus: TaskStatus, note?: string): Promise<Task> {
  return unwrap(
    apiClient.POST('/v1/crm/tasks/{id}/transition', {
      params: { path: { id } },
      body: { toStatus, ...(note !== undefined && note !== '' ? { note } : {}) },
    }),
  );
}

export async function deleteTask(id: string): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/crm/tasks/{id}', { params: { path: { id } } }));
}

export interface ListTodayTasksParams {
  readonly ownerUserId?: string | undefined;
  readonly limit?: number | undefined;
}

export async function listTodayTasks(
  params: ListTodayTasksParams = {},
): Promise<readonly TodayTask[]> {
  const result = await unwrap(
    apiClient.GET('/v1/crm/tasks/today', {
      params: {
        query: {
          limit: params.limit ?? 50,
          ...(params.ownerUserId !== undefined ? { ownerUserId: params.ownerUserId } : {}),
        },
      },
    }),
  );
  return result.items;
}

/** Ruční spuštění automatiky (jen vedoucí — `crm.task.generate`). */
export async function runAutoTaskGeneration(locale: 'cs' | 'en' = 'cs'): Promise<AutoTaskRunResult> {
  return unwrap(apiClient.POST('/v1/crm/tasks/auto', { body: { locale } }));
}
